import { StateError } from "./../state";
import { AlertService } from "../services/alert.service";
import { catchError } from "rxjs/operators";
import {
    HttpRequest,
    HttpHandler,
    HttpInterceptor,
    HttpEvent,
    HttpErrorResponse,
    HttpContextToken,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";

// A background request whose failure the user has nothing to do about (ID-10's profile refresh on resume and after
// login: the copy on screen simply stays) sets this, so a failure does not pop the error modal.
export const SILENT_ERRORS = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(private alertService: AlertService) {}

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                let stateError = StateError.Unknown;

                if (!req.context.get(SILENT_ERRORS) && (error.status || error.status === 0)) {
                    if (error.status.toString()[0] === "0") {
                        stateError = StateError.NoInternetAccess;
                        this.alertService.showConnectionError();
                    }
                    // 400 is deliberately excluded here: it's how expected, recoverable errors come back
                    // (wrong login credentials, validation failures, ...), and those are handled with
                    // their own targeted messaging where they're dispatched (e.g. auth.effects.ts's
                    // loginFail$ reads error.error.invalid_login and shows a specific "wrong credentials"
                    // alert) - a generic error box here would be redundant/confusing on top of that.
                    // A 401 on a request without a token is not a server fault either: nobody is signed in
                    // (ID-5; until ID-8 there is no anonymous session to send). The users-only features say so
                    // themselves.
                    const signedOut = error.status === 401 && !req.headers.has("Authorization");
                    if (
                        error.status.toString()[0] === "4" &&
                        error.status.toString() !== "400" &&
                        !signedOut
                    ) {
                        stateError = StateError.ServerError;
                        this.alertService.showServerError();
                    }
                    if (error.status.toString()[0] === "5") {
                        stateError = StateError.ServerError;
                        // TODO: Send to AppInsights via some LogService - no client-side error telemetry
                        // exists yet; needs picking/installing an Angular Application Insights SDK, which
                        // is real feature work, not a one-line fix.
                        this.alertService.showServerError();
                    }
                }

                // Spreading HttpErrorResponse only drops its prototype - the `headers` property still
                // references a live HttpHeaders instance, which NgRx's strictActionSerializability check
                // rejects once this error ends up in an action payload. Nothing downstream reads
                // `.headers`, so it's dropped here rather than sanitizing every effects file.
                const { headers, ...serializableError } = error;

                return throwError({
                    ...serializableError,
                    stateError,
                });
            })
        );
    }
}
